import { type FC, type PropsWithChildren, useMemo, useState } from "react";
import { useDebounce } from "use-debounce";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  useQueryBuilderJourneysQuery,
  useRelatedAudiencesSearchQuery,
  useParentModelForQueryBuilderQuery,
  useAudiencesByIdsQuery,
  type TraitDefinitionWithoutFilterableColumnsFragment,
  useTraitsForParentModelQuery,
} from "src/graphql";
import { isRealtimeEventModel } from "src/utils/event-models";
import { collectAudienceIds } from "./audience-id-collection";
import { mergeRelatedAudiences } from "./merge-related-audiences";
import { useQueryBuilderContext } from "src/components/explore/context/hooks";
import { ColumnDescriptionHelper } from "src/utils/column-descriptions";
import { getEventFromPull } from "src/components/traits/predictive-utils";
import {
  isRealtimeTrait,
  isTraitSupportedInRtaConditions,
} from "src/components/traits/utils";
import { isPresent } from "src/types/utils";
import partition from "lodash/partition";
import { useFetchFilterableAudienceColumns } from "src/hooks/use-fetch-filterable-audience-columns";
import {
  collectFilterableSegmentIds,
  getFilterableColumnsForSegment,
} from "src/hooks/hydrate-filterable-audience-columns";
import {
  buildConditionFromColumn,
  type ConditionBuilder,
} from "./build-condition-from-column";
import {
  FilterableOptionsContext,
  type FilterableOptionsContextValue,
  type FilterableOptionsData,
  type FilterableSection,
  useFilterableOptionsContext,
} from "./filterable-options-hook";
import { FilterOption } from "./constants";
import { isDenied } from "src/components/explore/restrictions";
import { getPredictorFeatureDefinitionForFilters } from "./predictive-feature-definitions";
import { shouldShowRelationship } from "./relationship-filtering";
import { filterAudiencesForSegmentSetPicker } from "src/components/audiences/rta-membership-reference";
import { isCepManagedModel } from "src/cep/utils";
import { buildTraitsById } from "./build-traits-by-id";

/** This hook fetches all filterable options for a given model. */
const useFilterableOptions = (
  modelId: string | null | undefined,
  visualQueryFilter: { conditions?: unknown[] } | null | undefined,
  debouncedAudienceSearch: string,
): FilterableOptionsData => {
  const {
    realtimeConditionScope,
    restrictions,
    filterableColumnsOverride,
    selectedEvent,
    realtimeEntryEvent,
    enableReferencingEntryEvent,
    audienceId,
    rootModelId,
    journeyTraits,
  } = useQueryBuilderContext();

  const {
    realtimeTraitsInRtaConditions = false,
    rtaWorkerWarehouseTraitMerge = false,
  } = useFlags();

  const isAudienceCreated = audienceId != null;

  // predictor_feature_definitions only exist on the parent model, so only
  // restrict key-event relationships while scoped to that root model.
  const restrictToPredictorKeyEvents =
    isDenied(restrictions, "non-key-events") && modelId === rootModelId;

  const { data: modelData, isLoading: isLoadingModel } =
    useParentModelForQueryBuilderQuery(
      { parentModelId: modelId! },
      { enabled: !!modelId },
    );

  const model = modelData?.segments_by_pk;
  const parentSnapshottingEnabled =
    model?.connection?.audience_snapshotting_enabled ?? false;
  const parentConnectionId = model?.connection?.id
    ? String(model.connection.id)
    : undefined;

  const { data: traitDefinitions, isLoading: isLoadingTraits } =
    useTraitsForParentModelQuery(
      { segmentId: modelId! },
      {
        enabled: !!modelId && !isDenied(restrictions, "traits"),
        select: (data) => data.trait_definitions,
      },
    );

  const { data: relatedAudiences, isLoading: isLoadingAudiences } =
    useRelatedAudiencesSearchQuery(
      {
        parentModelId: modelId ?? "",
        modelId: audienceId ? String(audienceId) : undefined,
        search: `%${debouncedAudienceSearch}%`,
        limit: 50,
      },
      {
        enabled: !!modelId && !isDenied(restrictions, "audiences"),
        select: (data) => data.segments,
        keepPreviousData: true,
      },
    );

  // Stable existence signal for gating the Audiences tab. Its variables never
  // change as the user types, so React Query caches it independently of the
  // debounced search above and it stays resolved across searches and reopens.
  const { data: existenceAudiences } = useRelatedAudiencesSearchQuery(
    {
      parentModelId: modelId ?? "",
      modelId: audienceId ? String(audienceId) : undefined,
      search: "%%",
      limit: 1,
    },
    {
      enabled: !!modelId && !isDenied(restrictions, "audiences"),
      select: (data) => data.segments,
    },
  );
  const hasReferenceableAudiences = (existenceAudiences?.length ?? 0) > 0;

  // Collect any audience IDs referenced in the current filter that are not
  // already covered by the pre-loaded relatedAudiences list.
  const referencedAudienceIds = useMemo(
    () => collectAudienceIds(visualQueryFilter),
    [visualQueryFilter],
  );

  const { data: hydratedAudiences, isLoading: isLoadingHydratedAudiences } =
    useAudiencesByIdsQuery(
      { ids: referencedAudienceIds },
      {
        enabled: referencedAudienceIds.length > 0,
        select: (data) => data.segments,
        staleTime: 1000 * 60 * 5,
      },
    );

  const mergedRelatedAudiences = useMemo(
    () => mergeRelatedAudiences(relatedAudiences, hydratedAudiences),
    [relatedAudiences, hydratedAudiences],
  );

  const { data: journeys, isLoading: isLoadingJourneys } =
    useQueryBuilderJourneysQuery(
      { parentModelId: modelId! },
      {
        enabled: !!modelId && !isDenied(restrictions, "journeys"),
        select: (data) => data.journeys,
      },
    );

  const predictorFeatureDefinition = useMemo(
    () =>
      getPredictorFeatureDefinitionForFilters(
        model?.predictor_feature_definitions,
      ),
    [model?.predictor_feature_definitions],
  );

  const predictorKeyEvents = useMemo(() => {
    const events =
      predictorFeatureDefinition?.predictor_feature_definition_events
        .map((e) => getEventFromPull(e.cached_event_pull))
        .filter(isPresent) ?? [];
    return new Set(events.map((e) => e.relationshipId.toString()));
  }, [predictorFeatureDefinition]);

  // Includes the parent model and all of its related models (even ones the
  // relationships memo below filters out of the UI) so any consumer can look
  // up columns for an arbitrary model id from one batched fetch.
  const filterableSegmentIds = useMemo(
    () => collectFilterableSegmentIds(modelId, model?.relationships),
    [modelId, model?.relationships],
  );

  const {
    filterableColumns: filterableColumnsByModelId,
    isLoading: isLoadingFilterableColumns,
  } = useFetchFilterableAudienceColumns(filterableSegmentIds);

  const originalColumns = model?.columns;
  const filterableColumns =
    filterableColumnsOverride ??
    (modelId
      ? getFilterableColumnsForSegment(filterableColumnsByModelId, modelId)
      : undefined);
  const dbtColumns = model?.query_dbt_model?.data?.columns;
  const sourceColumns = model?.source_column_descriptions;

  const columns = useMemo(() => {
    const columnDescriptionHelper = new ColumnDescriptionHelper({
      defaultModelId: model?.id,
      columns: originalColumns,
      dbtColumns,
      sourceColumns,
      filterableAudienceColumns: filterableColumns,
    });
    return (
      filterableColumns?.map((col) => {
        return {
          ...col,
          description: columnDescriptionHelper.getColumnDescription(
            col.name,
            col.model_id,
          ),
        };
      }) ?? []
    );
  }, [
    filterableColumns,
    dbtColumns,
    sourceColumns,
    originalColumns,
    model?.id,
  ]);

  const realtimeEntryRelationshipId =
    realtimeEntryEvent == null
      ? null
      : String(realtimeEntryEvent.relationshipId);
  const relationships = useMemo(
    () =>
      model?.relationships?.filter((r) =>
        shouldShowRelationship(r, {
          isAudienceCreated,
          realtimeConditionScope,
          restrictToPredictorKeyEvents,
          predictorKeyEvents,
          realtimeEntryRelationshipId,
        }),
      ) ?? [],
    [
      model?.relationships,
      realtimeConditionScope,
      restrictToPredictorKeyEvents,
      predictorKeyEvents,
      isAudienceCreated,
      realtimeEntryRelationshipId,
    ],
  );

  const { events, messageEvents } = useMemo(() => {
    const eventRelationships = (relationships ?? []).filter(
      ({ to_model: { event } }) => Boolean(event),
    );
    const [messageEvents, events] = partition(
      eventRelationships,
      ({ to_model: { cep_managed_model } }) => Boolean(cep_managed_model),
    );
    return {
      messageEvents,
      events: enableReferencingEntryEvent
        ? events
        : events.filter(
            ({ id }) =>
              selectedEvent?.relationshipId?.toString() !== id?.toString(),
          ),
    };
  }, [relationships, selectedEvent, enableReferencingEntryEvent]);

  const traits = useMemo(() => {
    if (isDenied(restrictions, "traits")) return [];
    // The query no longer selects the parent's filterable_audience_columns;
    // attach them from the batched fetch so trait consumers see a full parent.
    const parentModel = model
      ? {
          ...model,
          filterable_audience_columns: getFilterableColumnsForSegment(
            filterableColumnsByModelId,
            model.id,
          ),
        }
      : model;
    const traitsById = new Map(
      (traitDefinitions ?? []).map((trait) => [trait.id, trait]),
    );
    const customerTraits = (traitDefinitions ?? [])
      .filter((trait) => {
        switch (realtimeConditionScope) {
          case "journey-criteria":
            // The worker resolves journey criteria the same way as audience conditions.
            return isTraitSupportedInRtaConditions(trait, {
              warehouseTraitMergeEnabled: rtaWorkerWarehouseTraitMerge,
              traitsById,
            });
          case "audience":
            return realtimeTraitsInRtaConditions
              ? isTraitSupportedInRtaConditions(trait, {
                  warehouseTraitMergeEnabled: rtaWorkerWarehouseTraitMerge,
                  traitsById,
                })
              : !isRealtimeTrait(trait);
          case "journey-events":
          case undefined:
            return !isRealtimeTrait(trait);
          default:
            throw new Error(
              `Unknown realtime condition scope: ${realtimeConditionScope satisfies never}`,
            );
        }
      })
      .map((trait) => ({
        ...trait,
        parentModel,
      }));
    if (
      realtimeConditionScope === "journey-criteria" &&
      journeyTraits?.length
    ) {
      return [
        ...customerTraits,
        ...journeyTraits.map((trait) => ({
          ...trait,
          parentModel,
        })),
      ] as typeof customerTraits;
    }
    return customerTraits;
  }, [
    model,
    filterableColumnsByModelId,
    restrictions,
    traitDefinitions,
    realtimeTraitsInRtaConditions,
    rtaWorkerWarehouseTraitMerge,
    realtimeConditionScope,
    journeyTraits,
  ]);

  // Stays undefined until customer traits load so validation doesn't
  // misreport "not found" mid-fetch. An empty journey-trait list is loaded,
  // not "not yet fetched."
  const traitsById = useMemo(
    () =>
      buildTraitsById(
        traitDefinitions as
          | TraitDefinitionWithoutFilterableColumnsFragment[]
          | undefined,
        journeyTraits as
          | TraitDefinitionWithoutFilterableColumnsFragment[]
          | undefined,
      ),
    [traitDefinitions, journeyTraits],
  );

  const audiences = useMemo(() => {
    if (isDenied(restrictions, "audiences")) return [];
    return filterAudiencesForSegmentSetPicker(mergedRelatedAudiences, {
      audienceId,
      realtimeConditionScope,
    });
  }, [
    mergedRelatedAudiences,
    audienceId,
    realtimeConditionScope,
    restrictions,
  ]);

  const rawColumnMappings = model?.cep_workspace_config?.column_mappings as
    | Record<string, { column: string }>
    | null
    | undefined;

  const managedColumnNames = useMemo(() => {
    if (!rawColumnMappings) return new Set<string>();
    return new Set(Object.values(rawColumnMappings).map((m) => m.column));
  }, [rawColumnMappings]);

  const isParentModelLoading = !(modelId && model);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  const allData = useMemo(
    () => ({
      isLoading:
        isLoadingModel ||
        isLoadingFilterableColumns ||
        isLoadingTraits ||
        isLoadingAudiences ||
        isLoadingJourneys ||
        isLoadingHydratedAudiences,
      isParentModelLoading,
      modelId,
      modelName: model?.name,
      columns,
      events,
      messageEvents,
      relationships,
      traits,
      traitsById,
      relatedAudiences: audiences,
      hasReferenceableAudiences,
      parentSnapshottingEnabled,
      parentConnectionId,
      journeys: journeys ?? [],
      filterableColumnsByModelId,
      managedColumnNames,
    }),
    [
      audiences,
      hasReferenceableAudiences,
      parentSnapshottingEnabled,
      parentConnectionId,
      columns,
      events,
      messageEvents,
      filterableColumnsByModelId,
      isLoadingAudiences,
      isLoadingFilterableColumns,
      isLoadingHydratedAudiences,
      isLoadingJourneys,
      isLoadingModel,
      isLoadingTraits,
      isParentModelLoading,
      journeys,
      relationships,
      traits,
      traitsById,
      model?.name,
    ],
  );

  return allData;
};

type ProviderProps = PropsWithChildren<{
  modelId?: string | null;
  sections: FilterableSection[];
  /** Allow overriding the condition builder for special cases */
  conditionBuilder?: ConditionBuilder;
  parentFilterOptionType?: FilterableOptionsContextValue["parentFilterOptionType"];
  parentIconOverride?: FilterableOptionsContextValue["parentIconOverride"];
  semanticType?: string | null;
  /** The audience's saved filter, used to hydrate audience IDs referenced by SegmentSet conditions. */
  visualQueryFilter?: { conditions?: unknown[] } | null;
}>;

const MAX_NESTING_LEVEL = 3;

/**
 * Provides context-aware filterable options for a given model.
 * This should be rendered at the root and inside any nested filter
 * (like NumberOfFilter or EventFilter) to scope the context for child conditions.
 */
export const FilterableOptionsProvider: FC<Readonly<ProviderProps>> = ({
  modelId,
  sections: sectionsProp,
  conditionBuilder = buildConditionFromColumn,
  parentFilterOptionType,
  parentIconOverride,
  semanticType,
  visualQueryFilter,
  children,
}) => {
  const context = useFilterableOptionsContext();
  const nestingLevel = context.nestingLevel + 1;
  const [audienceSearch, setAudienceSearch] = useState("");
  const [debouncedAudienceSearch] = useDebounce(audienceSearch, 250);
  const data = useFilterableOptions(
    modelId,
    visualQueryFilter,
    debouncedAudienceSearch,
  );

  const {
    events,
    messageEvents,
    relationships,
    journeys,
    hasReferenceableAudiences,
    traits,
  } = data;

  /**
   * Derived from the parent context rather than taken as a prop so that every
   * nested filter — event, funnel, relation — picks it up without plumbing.
   */
  const isManagedModel = useMemo(
    () => isCepManagedModel({ modelId, relationships: context.relationships }),
    [context.relationships, modelId],
  );

  const parents: FilterableOptionsData[] = useMemo(() => {
    if (!context) return [];
    const {
      parents: parentsParents,
      buildCondition: _ignore,
      parentFilterOptionType: _ignore2,
      parentIconOverride: _ignore3,
      isCepManagedModel: _ignore4,
      ...data
    } = context;
    return [...parentsParents, data];
  }, [context]);

  const sections = useMemo(() => {
    let newSections = sectionsProp;
    if (nestingLevel >= MAX_NESTING_LEVEL) {
      newSections = newSections.filter(
        (s) =>
          s.type !== FilterOption.Relation && s.type !== FilterOption.Event,
      );
    }
    // If a tab is empty, remove it
    // TODO(nolan): this logic is duplicated with the logic in `useConditionSelect`
    // that decides what to render per tab. Should be merged
    newSections = newSections.filter((s) => {
      switch (s.type) {
        case FilterOption.Event:
          return events.length > 0;
        case FilterOption.Relation:
          return relationships.filter((r) => !r.to_model.event).length > 0;
        case FilterOption.Journey:
          return journeys.length > 0;
        case FilterOption.Audience:
          // Keep the tab while searching so a zero-result search doesn't make
          // it disappear mid-typing.
          return hasReferenceableAudiences || audienceSearch.length > 0;
        case FilterOption.RealtimeEvent:
          return (
            events.filter((e) => isRealtimeEventModel(e.to_model.event))
              .length > 0
          );
        case FilterOption.Trait:
        case FilterOption.TraitTemplate:
          // At root level, inline trait option is always injected
          return traits.length > 0 || nestingLevel === 0;
        case FilterOption.InlineTrait:
        case FilterOption.EventProperty:
        case FilterOption.All:
        case FilterOption.Property:
        case FilterOption.JourneyMemberState:
        case FilterOption.JourneyVariable:
          return true;
        case FilterOption.MessageEvent:
          return messageEvents.length > 0;
        default:
          throw new Error(`Unknown type: ${s.type satisfies never}`);
      }
    });
    return newSections;
  }, [
    sectionsProp,
    nestingLevel,
    events,
    traits.length,
    relationships,
    journeys.length,
    hasReferenceableAudiences,
    messageEvents.length,
    audienceSearch.length,
  ]);

  const value = useMemo(
    () => ({
      ...data,
      parents,
      sections,
      buildCondition: conditionBuilder,
      parentFilterOptionType,
      isCepManagedModel: isManagedModel,
      parentIconOverride,
      semanticType,
      nestingLevel,
      setAudienceSearch,
    }),
    [
      data,
      sections,
      conditionBuilder,
      nestingLevel,
      parentFilterOptionType,
      isManagedModel,
      parentIconOverride,
      semanticType,
      parents,
    ],
  );

  return (
    <FilterableOptionsContext.Provider value={value}>
      {children}
    </FilterableOptionsContext.Provider>
  );
};
